/**
 * Google Address Validation API Service
 * 
 * Validates addresses using Google's Address Validation API to ensure
 * addresses are deliverable and properly formatted.
 * 
 * Falls back gracefully if validation fails - users can still submit.
 */

import { config } from '../config/config';

export interface AddressValidationInput {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  /** ISO-3166-1 alpha-2 country code. Defaults to 'US' when omitted. */
  country?: string;
}

export interface AddressValidationResult {
  isValid: boolean;
  validationStatus: 'validated' | 'needs_correction' | 'fallback' | 'pending';
  confidence: 'high' | 'medium' | 'low' | 'unknown';
  correctedAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  formattedAddress?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  message?: string;
  errorCode?: string;
}

interface GoogleAddressValidationResponse {
  result?: {
    verdict?: {
      inputGranularity?: string;
      validationGranularity?: string;
      geocodeGranularity?: string;
      addressComplete?: boolean;
      hasUnconfirmedComponents?: boolean;
      hasInferredComponents?: boolean;
      hasReplacedComponents?: boolean;
    };
    address?: {
      formattedAddress?: string;
      postalAddress?: {
        regionCode?: string;
        languageCode?: string;
        postalCode?: string;
        administrativeArea?: string;
        locality?: string;
        addressLines?: string[];
      };
      addressComponents?: Array<{
        componentName?: {
          text?: string;
          languageCode?: string;
        };
        componentType?: string;
        confirmationLevel?: string;
      }>;
    };
    geocode?: {
      location?: {
        latitude?: number;
        longitude?: number;
      };
      plusCode?: {
        globalCode?: string;
      };
    };
    uspsData?: {
      standardizedAddress?: {
        firstAddressLine?: string;
        secondAddressLine?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        zipCodeExtension?: string;
      };
      deliveryPointCode?: string;
      dpvConfirmation?: string;
    };
  };
  responseId?: string;
}

/**
 * Cache for validation results to avoid duplicate API calls
 */
const validationCache = new Map<string, AddressValidationResult>();

/**
 * Generate a cache key from address components
 */
function getCacheKey(address: AddressValidationInput): string {
  return `${address.addressLine1}|${address.addressLine2 || ''}|${address.city}|${address.state}|${address.zipCode}|${address.country || 'US'}`.toLowerCase().trim();
}

/**
 * Validate an address using Google Address Validation API
 * 
 * @param address - The address to validate
 * @returns Validation result with status, corrections, and coordinates
 */
export async function validateAddress(address: AddressValidationInput): Promise<AddressValidationResult> {
  const country = address.country || 'US';
  const cacheKey = getCacheKey(address);

  // Check cache first
  if (validationCache.has(cacheKey)) {
    console.log('📍 Using cached address validation result');
    return validationCache.get(cacheKey)!;
  }

  // Return pending if missing required fields
  if (!address.addressLine1?.trim() || !address.city?.trim() || !address.state?.trim() || !address.zipCode?.trim()) {
    return {
      isValid: false,
      validationStatus: 'pending',
      confidence: 'unknown',
      message: 'Missing required address fields'
    };
  }

  try {
    console.log('🔍 Validating address with Google Address Validation API...');

    // Add timeout controller - fail gracefully after 5 seconds
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const requestBody: Record<string, unknown> = {
      address: {
        regionCode: country,
        addressLines: [
          address.addressLine1,
          address.addressLine2 || ''
        ].filter(Boolean),
        locality: address.city,
        administrativeArea: address.state,
        postalCode: address.zipCode
      }
    };
    // USPS CASS processing is only available for US addresses
    if (country === 'US') {
      requestBody.enableUspsCass = true;
    }

    const response = await fetch(
      `https://addressvalidation.googleapis.com/v1:validateAddress?key=${config.googleMapsApiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      }
    );
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('⚠️ Address validation API error:', response.status, errorText);
      
      // Treat API errors as passable — don't block the user
      const fallbackResult: AddressValidationResult = {
        isValid: true,
        validationStatus: 'fallback',
        confidence: 'unknown',
        message: 'Address verification is temporarily unavailable.',
        errorCode: `HTTP_${response.status}`
      };
      
      validationCache.set(cacheKey, fallbackResult);
      return fallbackResult;
    }

    const data: GoogleAddressValidationResponse = await response.json();
    const result = parseValidationResponse(data, address, country);
    
    // Cache the result
    validationCache.set(cacheKey, result);
    
    console.log('✅ Address validation complete:', result.validationStatus, result.confidence);
    return result;

  } catch (error) {
    // Handle timeout specifically — don't block the user
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn('⏱️ Address validation timed out after 5 seconds');
      return {
        isValid: true,
        validationStatus: 'fallback',
        confidence: 'unknown',
        message: 'Address verification timed out.',
        errorCode: 'TIMEOUT'
      } satisfies AddressValidationResult;
    }
    
    console.error('❌ Address validation error:', error);
    
    // Don't block the user on validation infrastructure failures
    return {
      isValid: true,
      validationStatus: 'fallback',
      confidence: 'unknown',
      message: 'Could not verify address.',
      errorCode: error instanceof Error ? error.message : 'UNKNOWN_ERROR'
    } satisfies AddressValidationResult;
  }
}

/**
 * Check if address line contains a street number
 * Street numbers typically start the address (e.g., "123 Main St")
 */
function hasStreetNumber(addressLine: string): boolean {
  if (!addressLine) return false;
  const trimmed = addressLine.trim();
  // Check if starts with a number (most common: "123 Main St")
  // Also handles ranges like "123-125 Main St" or "123A Main St"
  return /^\d+/.test(trimmed);
}

/**
 * Parse the Google Address Validation API response
 */
function parseValidationResponse(
  response: GoogleAddressValidationResponse,
  originalAddress: AddressValidationInput,
  country: string = 'US'
): AddressValidationResult {
  const verdict = response.result?.verdict;
  const geocode = response.result?.geocode;
  const uspsData = response.result?.uspsData;
  const formattedAddress = response.result?.address?.formattedAddress;

  // Determine validation status
  let validationStatus: AddressValidationResult['validationStatus'] = 'validated';
  let confidence: AddressValidationResult['confidence'] = 'high';
  let isValid = true;
  let message: string | undefined;

  // FIRST: Check if address has a street number - this is required for a deliverable address
  if (!hasStreetNumber(originalAddress.addressLine1)) {
    return {
      isValid: false,
      validationStatus: 'needs_correction',
      confidence: 'low',
      message: 'Please include a street number (e.g., 123 Main St)',
      coordinates: geocode?.location ? {
        lat: geocode.location.latitude!,
        lng: geocode.location.longitude!
      } : undefined
    };
  }

  if (country === 'US') {
    // Check USPS DPV confirmation (best indicator for US addresses)
    const dpvConfirmation = uspsData?.dpvConfirmation;
    if (dpvConfirmation) {
      switch (dpvConfirmation) {
        case 'Y':
          validationStatus = 'validated';
          confidence = 'high';
          break;
        case 'S':
          validationStatus = 'validated';
          confidence = 'medium';
          message = 'Address confirmed (secondary component not validated)';
          break;
        case 'D':
          validationStatus = 'validated';
          confidence = 'medium';
          break;
        case 'N':
          isValid = false;
          validationStatus = 'needs_correction';
          confidence = 'low';
          message = 'This address could not be confirmed. Please check the street, city, and ZIP code.';
          break;
        default:
          validationStatus = 'validated';
          confidence = 'medium';
      }
    } else {
      // No USPS data — fall back to verdict
      if (verdict?.addressComplete === false) {
        isValid = false;
        validationStatus = 'needs_correction';
        confidence = 'low';
        message = 'This address appears incomplete. Please check all fields.';
      } else if (verdict?.hasUnconfirmedComponents) {
        validationStatus = 'needs_correction';
        confidence = 'low';
        message = 'Some address components could not be verified';
      } else if (verdict?.hasReplacedComponents || verdict?.hasInferredComponents) {
        validationStatus = 'validated';
        confidence = 'medium';
      }
    }
  } else {
    // Non-US (e.g. CA): USPS data is not returned — use verdict directly
    if (verdict?.addressComplete === false) {
      isValid = false;
      validationStatus = 'needs_correction';
      confidence = 'low';
      message = 'This address appears incomplete. Please check all fields.';
    } else if (verdict?.hasUnconfirmedComponents) {
      validationStatus = 'needs_correction';
      confidence = 'low';
      message = 'Some address components could not be verified';
    } else if (verdict?.hasReplacedComponents || verdict?.hasInferredComponents) {
      validationStatus = 'validated';
      confidence = 'medium';
    } else if (verdict?.addressComplete) {
      // addressComplete=true with no issues → high confidence
      validationStatus = 'validated';
      confidence = 'high';
    }
  }

  // Build corrected address from USPS standardized data if available (US only)
  let correctedAddress: AddressValidationResult['correctedAddress'] | undefined;
  if (country === 'US' && uspsData?.standardizedAddress) {
    const std = uspsData.standardizedAddress;
    correctedAddress = {
      addressLine1: std.firstAddressLine || originalAddress.addressLine1,
      addressLine2: std.secondAddressLine || originalAddress.addressLine2,
      city: std.city || originalAddress.city,
      state: std.state || originalAddress.state,
      zipCode: std.zipCodeExtension
        ? `${std.zipCode}-${std.zipCodeExtension}`
        : (std.zipCode || originalAddress.zipCode)
    };
  }

  // Get coordinates
  const coordinates = geocode?.location ? {
    lat: geocode.location.latitude!,
    lng: geocode.location.longitude!
  } : undefined;

  return {
    isValid,
    validationStatus,
    confidence,
    correctedAddress,
    formattedAddress,
    coordinates,
    message
  };
}

/**
 * Quick validation check - returns true if address passes basic validation
 * This is a lighter-weight check that can be used inline
 */
export async function isAddressValid(address: AddressValidationInput): Promise<boolean> {
  const result = await validateAddress(address);
  return result.isValid;
}

/**
 * Clear the validation cache
 */
export function clearValidationCache(): void {
  validationCache.clear();
}

/**
 * Get a cached validation result if available
 */
export function getCachedValidation(address: AddressValidationInput): AddressValidationResult | null {
  const cacheKey = getCacheKey(address);
  return validationCache.get(cacheKey) || null;
}
